import { tx } from "@/i18n/tx";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, API_BASE, downloadFile, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Download, ShieldAlert, FileBarChart, CheckCircle2 } from "lucide-react";

const EXPORT_KEY = "oresto-export-downloaded";

const lastMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
};

export function MonthlyReportCard() {
  const [month, setMonth] = useState(lastMonth());
  const dl = () => downloadFile(`${API_BASE}/reports/monthly.pdf?month=${month}`, `oresto-rapport-${month}.pdf`)
    .then(() => toast.success(tx("ui_accountcards.k86d31c"))).catch(() => toast.error(tx("ui_accountcards.kf91b43")));
  return (
    <div className="card-c" data-testid="monthly-report-card">
      <div className="flex items-center gap-2 mb-1">
        <FileBarChart className="w-5 h-5 text-secondary-c" />
        <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold">{tx("ui_accountcards.k236b32")}</p>
      </div>
      <p className="text-[11px] text-text-secondary mb-3">{tx("ui_accountcards.k42701e")}</p>
      <div className="flex gap-2">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="input-c text-sm flex-1 min-w-0" data-testid="monthly-report-month" />
        <button onClick={dl} className="btn-primary !py-2 !px-4 text-sm shrink-0" data-testid="monthly-report-download">PDF</button>
      </div>
    </div>
  );
}

export function AccountDataCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [kept, setKept] = useState(false);
  const [exported, setExported] = useState(() => sessionStorage.getItem(EXPORT_KEY) === "1");
  const [busy, setBusy] = useState(false);

  const exportAll = () => downloadFile(`${API_BASE}/account/export`, "oresto-export.zip")
    .then(() => { sessionStorage.setItem(EXPORT_KEY, "1"); setExported(true); toast.success(t("account.export_done")); })
    .catch(() => toast.error(tx("ui_accountcards.kb323e5")));

  const del = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/account/delete", { password, confirm, export_kept: kept });
      sessionStorage.removeItem(EXPORT_KEY);
      toast.success(t("account.deleted"));
      window.location.href = "/";
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("account.error"));
      setBusy(false);
    }
  };
  const canDelete = exported && kept && confirm.trim().toUpperCase() === "SUPPRIMER" && !busy;

  return (
    <div className="card-c" data-testid="account-data-card">
      <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold">{tx("ui_accountcards.k61d8c4")}</p>
      <button onClick={exportAll} className="mt-3 w-full flex items-center gap-3 p-3 bg-surface-muted rounded-xl text-left text-sm hover:bg-[#ece8da] transition-colors" data-testid="account-export-btn">
        <Download className="w-5 h-5 text-primary-c shrink-0" />
        <span className="flex-1"><span className="font-semibold block">{tx("ui_accountcards.k806e76")}</span><span className="text-[11px] text-text-secondary">{tx("ui_accountcards.k625343")}</span></span>
      </button>
      {!open ? (
        <button onClick={() => setOpen(true)} className="mt-2 w-full flex items-center gap-3 p-3 rounded-xl text-left text-sm text-red-600 hover:bg-red-50 transition-colors" data-testid="account-delete-open">
          <ShieldAlert className="w-5 h-5 shrink-0" /> <span className="font-semibold">{t("account.open")}</span>
        </button>
      ) : (
        <form onSubmit={del} className="mt-3 p-3 rounded-xl border border-red-200 bg-red-50/60 space-y-3" data-testid="account-delete-form">
          <p className="text-sm text-red-800 font-semibold leading-snug" data-testid="account-delete-warning">{t("account.delete_warning")}</p>
          <div className="space-y-1.5">
            <p className="text-xs text-text-secondary">{t("account.export_step")}</p>
            <button type="button" onClick={exportAll} className={`w-full min-h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ${exported ? "bg-green-100 text-green-800" : "bg-white border border-c"}`} data-testid="account-delete-export-btn">
              {exported ? <CheckCircle2 className="w-4 h-4" /> : <Download className="w-4 h-4" />}{exported ? t("account.export_done") : t("account.export_btn")}
            </button>
            {!exported && <p className="text-[11px] text-red-700" data-testid="account-delete-export-missing">{t("account.export_missing")}</p>}
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={kept} onChange={(e) => setKept(e.target.checked)} className="w-5 h-5 mt-0.5 accent-red-600 shrink-0" data-testid="account-delete-kept-checkbox" />
            <span>{t("account.export_kept")}</span>
          </label>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("account.password")} className="input-c text-sm" data-testid="account-delete-password" />
          <input required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t("account.type_confirm")} className="input-c text-sm" data-testid="account-delete-confirm" />
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => { setOpen(false); navigate("/app/plus"); }} className="btn-secondary !py-2 text-sm">{t("account.cancel")}</button>
            <button disabled={!canDelete} className="py-2 rounded-xl bg-red-600 text-white text-sm font-semibold disabled:opacity-50" data-testid="account-delete-submit">{t("account.delete_btn")}</button>
          </div>
        </form>
      )}
    </div>
  );
}
